import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { NumReturnsOption } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type {
  DateSubconditionState,
  NumCustomerReturnsState,
} from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo } from "react";
import { DateSubconditionSelector } from "./date-subcondition-selector";

export const NumCustomerReturnsDetails = memo(
  function NumCustomerReturnsDetails({
    state,
    setState,
    disabled,
  }: ConditionTypeDetailsProps<NumCustomerReturnsState>) {
    const { t } = useTranslation();
    const returnOptions: RedoListItem<NumReturnsOption>[] = useMemo(
      () =>
        Object.values(NumReturnsOption).map((option) => ({
          id: option,
          value: option,
          type: "text" as const,
          text:
            option === NumReturnsOption.LESS_THAN
              ? t("Less than")
              : t("Greater than"),
        })),
      [t],
    );

    const selectedOption = state.returnOption
      ? returnOptions.find((option) => option.value === state.returnOption)
      : undefined;

    // Handler for date subcondition changes
    const handleDateSubconditionChange = useHandler(
      (updatedDateSubcondition: DateSubconditionState) => {
        setState({ ...state, dateSubcondition: updatedDateSubcondition });
      },
    );

    return (
      <>
        <RedoSingleSelectDropdownInput
          disabled={disabled}
          flexGrow
          label={t("Comparison")}
          options={returnOptions}
          optionSelected={({ item }) =>
            setState({ ...state, returnOption: item.value })
          }
          placeholder={t("Select comparison...")}
          selectedItem={selectedOption}
          size="xs"
        />
        <RedoTextInput
          label={t("Number of returns")}
          min={0}
          onBlur={() =>
            setState({ ...state, numReturns: (+state.numReturns).toFixed(0) })
          }
          setValue={(e) => setState({ ...state, numReturns: e })}
          state={disabled ? "readonly" : "default"}
          value={state.numReturns}
        />

        {/* Use shared date subcondition selector */}
        <DateSubconditionSelector
          disabled={disabled}
          onChange={handleDateSubconditionChange}
          state={state.dateSubcondition}
        />
      </>
    );
  },
);
